/**
 * Encode pairing data for URL (base64url)
 * @param {string} secret - Base32 encoded TOTP secret
 * @param {string} nameA - First person's name
 * @param {string} nameB - Second person's name
 * @returns {string} Base64url encoded payload
 */
export function encodePayload(secret, nameA, nameB) {
  const data = { s: secret, a: nameA, b: nameB };
  const json = JSON.stringify(data);
  return btoa(json)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Decode pairing data from URL
 * @param {string} encoded - Base64url encoded payload
 * @returns {{ secret: string, nameA: string, nameB: string } | null} Decoded payload or null if invalid
 */
export function decodePayload(encoded) {
  try {
    // Add padding if needed
    const padded = encoded + '==='.slice(0, (4 - encoded.length % 4) % 4);
    const base64 = padded
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const json = atob(base64);
    const data = JSON.parse(json);

    // Validate required fields
    if (!data.s || typeof data.s !== 'string') return null;
    if (!data.a || typeof data.a !== 'string') return null;
    if (!data.b || typeof data.b !== 'string') return null;

    return {
      secret: data.s,
      nameA: data.a,
      nameB: data.b,
    };
  } catch (e) {
    console.error('Failed to decode payload:', e);
    return null;
  }
}

/**
 * Parse current URL hash to determine view and data
 * @returns {{ view: 'create' | 'pairing', secret?: string, nameA?: string, nameB?: string }}
 */
export function parseHash() {
  const hash = window.location.hash.slice(1); // remove #

  if (!hash || hash === '/' || hash === '/new') {
    return { view: 'create' };
  }

  if (hash.startsWith('/p/')) {
    const payload = decodePayload(hash.slice(3));
    if (payload) {
      return { view: 'pairing', ...payload };
    }
  }

  // Invalid hash, default to create view
  return { view: 'create' };
}

/**
 * Build the full URL for a pairing
 * @param {string} secret - Base32 encoded TOTP secret
 * @param {string} nameA - First person's name
 * @param {string} nameB - Second person's name
 * @returns {string} Full URL with hash
 */
export function buildPairingURL(secret, nameA, nameB) {
  const payload = encodePayload(secret, nameA, nameB);
  const baseUrl = window.location.origin + window.location.pathname;
  return `${baseUrl}#/p/${payload}`;
}
